import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
// Straight from the files, not the constants barrel: the barrel loads every
// page through MenuConstants, and MenuConstants loads this page.
import { APP_ROUTE_LEARNING_JOURNEY_PROGRAMS_MILESTONES } from "../../../constants/RouteConstants";
import { LEARNING_STATUS_STARTED } from "../../../constants/GlobalConstants";

dayjs.extend(utc);
dayjs.extend(timezone);

// GET /self/rewards: what the badge was awarded for.
export const ENTITY_TYPE_LEARNING_OBJECT = 1;
export const ENTITY_TYPE_PROGRAM = 2;

// GET /self/points/history: PointSource on the API.
const POINTS_SOURCE_PROGRAM = 2;
const PROGRAM_DESCRIPTION = /^completed learning pathway/i;

// The API sends UTC times without a zone marker. Times are shown in the
// learner's time zone; the zone argument is for tests.
export const toLocal = (value, timeZone) =>
  timeZone ? dayjs.utc(value).tz(timeZone) : dayjs.utc(value).local();

// DD MMM YYYY, with the month named in the app's locale.
export const formatDate = (intl, value, timeZone) => {
  const parts = intl.formatDateToParts(dayjs.utc(value).toDate(), {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone,
  });
  const part = (type) => parts.find((p) => p.type === type)?.value;
  return `${part("day")} ${part("month")} ${part("year")}`;
};

const time = (value) => toLocal(value).valueOf();

export const sortRewardsNewestFirst = (rewards = []) =>
  [...rewards].sort((a, b) => time(b.awardedAt) - time(a.awardedAt));

// The API always sends `source`; the description (English, written by the
// API) is only a fallback for an entry without one. Do not build on it.
export const isProgramPoints = (entry) => {
  if (entry?.source !== undefined && entry?.source !== null) {
    return (
      Number(entry.source) === POINTS_SOURCE_PROGRAM ||
      String(entry.source).toLowerCase() === "learningpathway"
    );
  }
  return PROGRAM_DESCRIPTION.test(entry?.description || "");
};

const pointsOf = (entry) => Number(entry?.points) || 0;

export const totalPoints = (entries = []) =>
  entries.reduce((sum, entry) => sum + pointsOf(entry), 0);

// The points earned in the learner's current calendar month.
export const pointsThisMonth = (entries = [], now = dayjs(), timeZone) => {
  const month = (value) => toLocal(value, timeZone).format("YYYY-MM");
  const current = month(now);
  return totalPoints(entries.filter((entry) => month(entry.earnedAt) === current));
};

// Every entry with the learner's total after it, counted in time order over
// all of the learner's points, returned newest first. Entries earned at the
// same moment keep the API's newest-first order.
export const withRunningTotals = (entries = []) => {
  let runningTotal = 0;
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => time(a.entry.earnedAt) - time(b.entry.earnedAt) || b.index - a.index)
    .map(({ entry }) => {
      runningTotal += pointsOf(entry);
      return { ...entry, runningTotal };
    })
    .reverse();
};

export const dayKey = (value) => toLocal(value).format("YYYY-MM-DD");

// Newest-first entries in one group per local calendar day.
export const groupByDay = (entries = []) =>
  entries.reduce((groups, entry) => {
    const key = dayKey(entry.earnedAt);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.entries.push(entry);
    } else {
      groups.push({ key, date: toLocal(entry.earnedAt), entries: [entry] });
    }
    return groups;
  }, []);

// Only Started training is in progress: InComplete means failed, and the
// API has no InProgress enrolment status.
const isInProgress = (enrolment) =>
  (enrolment?.status || "").toLowerCase() === LEARNING_STATUS_STARTED.toLowerCase();

const byDueDate = (a, b) => {
  if (!a.endDate || !b.endDate) {
    return (a.endDate ? 0 : 1) - (b.endDate ? 0 : 1);
  }
  return time(a.endDate) - time(b.endDate);
};

// The training to continue: Started, Required before Optional, the
// earliest due date first.
export const pickContinueEnrolment = (enrolments = []) =>
  (enrolments || [])
    .filter(isInProgress)
    .sort((a, b) => Number(!!a.isOptional) - Number(!!b.isOptional) || byDueDate(a, b))[0] ||
  null;

export const findEnrolment = (enrolments = [], programId) =>
  (enrolments || []).find((enrolment) => enrolment?.learningPathway?.id === programId);

export const learningJourneyPath = (enrolmentId) =>
  APP_ROUTE_LEARNING_JOURNEY_PROGRAMS_MILESTONES.replace(":id", enrolmentId);

export const imageUrl = (artifactUrl) =>
  artifactUrl ? new URL(artifactUrl, process.env.REACT_APP_URL).toString() : null;
